import {useMemo} from "react";
import {AvailableLoadsByTimeProps} from "./AvailableLoadsByTimeInsight";
import {getDateFormatterOptions, TimeBucketData, TimeChartData} from "../../chart/TimeChart/TimeChart";
import {
  addTime,
  adjustFromTimeZone,
  getDefaultTimeFrame,
  getTimeFrameDates,
  isBefore,
  startOf
} from "@ots-frontend/ots-data";

export type UseAvailableLoadsByTimeInsightProps = Omit<AvailableLoadsByTimeProps, 'id' | 'title' | 'showEmpty' | 'xAxisTitle' | 'yAxisTitle'>

export type UseAvailableLoadsByTimeInsightResult = {
  chartData?: TimeChartData
}

export function useAvailableLoadsByTimeInsight(props: UseAvailableLoadsByTimeInsightProps): UseAvailableLoadsByTimeInsightResult {
  const {
    fromDttm,
    toDttm = new Date().getTime(),
    data,
    category,
  } = props

  const timeFrame = useMemo(() => {
    const defaultTimeFrame = getDefaultTimeFrame(fromDttm, toDttm)
    if (defaultTimeFrame === 'MONTH' || defaultTimeFrame === 'WEEK') {
      return "DAY"
    }
    return defaultTimeFrame
  }, [fromDttm, toDttm])

  const chartData: TimeChartData | undefined = useMemo(() => {
    if (!data) {
      return undefined
    }

    const dates: string[] = []
    const buckets: Record<string, TimeBucketData> = {}

    const timeFrameDates = getTimeFrameDates(timeFrame, fromDttm, toDttm)

    let startDttm = timeFrameDates.startDttm
    const endDttm = timeFrameDates.endDttm

    const formatter = new Intl.DateTimeFormat(undefined, getDateFormatterOptions(timeFrame))

    while (isBefore(startDttm, endDttm)) {
      dates.push(formatter.format(startDttm))

      data.forEach((snapshot) => {
        if (!category || category.cargoSubtype === snapshot.cargoSubtype) {
          const objectName = snapshot.objectName ?? "Unknown"
          const count = snapshot.entries.find((entry) => startOf(timeFrame, adjustFromTimeZone(entry.dttm)).getTime() === startDttm.getTime())?.count

          if (objectName in buckets) {
            buckets[objectName].values.push(count)
          } else {
            buckets[objectName] = {
              values: [count]
            }
          }
        }
      })

      startDttm = addTime(timeFrame, startDttm, 1)
    }

    return {
      titles: dates,
      buckets: buckets,
    }
  }, [data, timeFrame, fromDttm, toDttm, category])

  return {
    chartData: chartData
  };
}
